import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

export default function ConfirmEmail() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'confirming' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        console.log('🔍 [CONFIRM_EMAIL] Starting email confirmation process...');
        
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const email = urlParams.get('email');
        const plan = urlParams.get('plan');
        
        console.log('📊 [CONFIRM_EMAIL] URL parameters:', {
          email,
          plan,
          fullUrl: window.location.href
        });

        if (!email) {
          console.log('❌ [CONFIRM_EMAIL] No email found in URL');
          setStatus('error');
          setMessage('Email no encontrado en la URL');
          return;
        }

        setStatus('loading');
        setMessage('Confirmando tu email...');

        // Always fetch fresh user data from database to get current plan status
        console.log('🔍 [CONFIRM_EMAIL] Fetching fresh user data from database for email:', email);
        const { data: userData, error } = await supabase
          .from('users')
          .select('*')
          .eq('email', email)
          .single();

        console.log('📊 [CONFIRM_EMAIL] Database query result:', {
          userData,
          error,
          hasData: !!userData
        });

        if (error || !userData) {
          console.error('❌ [CONFIRM_EMAIL] User not found:', error);
          setStatus('error');
          setMessage('Usuario no encontrado');
          return;
        }
        
        const currentUserData = userData;
        setUserData(userData);
        console.log('✅ [CONFIRM_EMAIL] Fresh user data from database:', {
          id: userData.id,
          email: userData.email,
          plan: userData.plan,
          plan_status: userData.plan_status,
          subscription_status: userData.subscription_status,
          stripe_customer_id: userData.stripe_customer_id,
          trial_end_date: userData.trial_end_date,
          email_confirm: userData.email_confirm
        });

        // Update email_confirm to true in database
        const { error: updateError } = await supabase
          .from('users')
          .update({ email_confirm: true })
          .eq('email', email);

        if (updateError) {
          console.error('❌ Error updating email confirmation:', updateError);
          setStatus('error');
          setMessage('Error al confirmar el email en la base de datos');
          return;
        }

        console.log('✅ Email confirmation updated in database');
        
        // Immediately auto-login user and redirect to dashboard
        if (currentUserData && currentUserData.id) {
          console.log('🔄 [CONFIRM_EMAIL] Auto-logging user and redirecting to dashboard');
          
          // Create user session for auto-login
          const sessionData = {
            id: currentUserData.id,
            email: currentUserData.email,
            plan: currentUserData.plan,
            plan_status: currentUserData.plan_status,
            subscription_status: currentUserData.subscription_status,
            trial_end_date: currentUserData.trial_end_date,
            shelter_name: currentUserData.shelter_name,
            city: currentUserData.city,
            state: currentUserData.state,
            phone: currentUserData.phone,
            loginTime: Date.now(),
            expiresAt: Date.now() + (3 * 60 * 60 * 1000) // 3 hours
          };
          
          // Save session to localStorage
          localStorage.setItem('user_session', JSON.stringify(sessionData));
          localStorage.setItem('user_data', JSON.stringify(currentUserData));
          
          console.log('✅ [CONFIRM_EMAIL] User session created:', sessionData);
          console.log('🔄 [CONFIRM_EMAIL] Redirecting to admin dashboard');
          
          // Redirect to admin dashboard immediately
          navigate('/admin');
        } else {
          setStatus('error');
          setMessage('Error: No se pudo obtener los datos del usuario');
        }

      } catch (error) {
        console.error('Error confirming email:', error);
        setStatus('error');
        setMessage('Error al confirmar el email');
      }
    };

    handleEmailConfirmation();
  }, []);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return '⏳';
      case 'confirming':
        return '📧';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-blue-600';
      case 'confirming':
        return 'text-orange-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Header */}
          <div className="mb-8">
            <img 
              src="/images/image.png" 
              alt="Huellitas Unidas Logo" 
              className="w-16 h-16 object-contain mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
              Huellitas Unidas
            </h1>
            <p className="text-sm text-gray-600">Salvando vidas, una patita a la vez</p>
          </div>

          {/* Status */}
          <div className="mb-6">
            <div className="text-6xl mb-4">{getStatusIcon()}</div>
            <h2 className={`text-xl font-semibold mb-2 ${getStatusColor()}`}>
              {status === 'loading' && 'Cargando...'}
              {status === 'confirming' && 'Confirmando Email'}
              {status === 'success' && '¡Email Confirmado!'}
              {status === 'error' && 'Error'}
            </h2>
            <p className="text-gray-600">{message}</p>
          </div>

          {/* Loading Spinner */}
          {(status === 'loading' || status === 'confirming') && (
            <div className="flex justify-center mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          )}

          {/* Success Message */}
          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">
                ¡Perfecto! Tu email ha sido confirmado exitosamente. 
                Ahora serás redirigido a Stripe para completar tu suscripción.
              </p>
            </div>
          )}

          {/* Error Message */}
          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">{message}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Intentar Nuevamente
              </button>
            </div>
          )}

          {/* User Info */}
          {userData && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-800 mb-2">Detalles de tu cuenta:</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Refugio:</strong> {userData.shelter_name}</p>
                <p><strong>Plan:</strong> Sin plan activo</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-xs text-gray-500">
            <p>Si tienes problemas, contacta a soporte técnico</p>
          </div>
        </div>
      </div>
    </div>
  );
}
